import { getQueryKey } from "@core/hooks/useSession";
import { APIErrorType } from "@core/services/nocd-api";
import type { Page, PageProps } from "@core/types";
import { SSRError } from "@core/types";
import getServerSession from "@core/utils/getServerSession";
import { getPublicPosts, getPublicPostsQueryKey } from "@features/community-v2";
import {
  fetchAllTopics,
  getQueryKey as subtypeQueryKey,
} from "@features/community-v2/hooks/useSubtypes";
import {
  getCategorizedTopics,
  getTopicsQueryKey,
} from "@features/community-v2/hooks/useTopics";
import SubtypeFeedScreen from "@features/community-v2/screens/SubtypeFeedScreen";
import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import React from "react";
import { QueryClient } from "react-query";
import { dehydrate } from "react-query/hydration";

interface Props extends PageProps {
  segmentId: string | null;
  subtypeId: string;
  title: string;
  slug: string;
  ssrError: SSRError;
}

const SubtypeFeedPage: Page<Props> = ({
  segmentId,
  subtypeId,
  title,
  slug,
  ssrError,
}) => {
  const metaTitle = `${title} Forum | ${title} Community`;
  const metaDescription = `The NOCD Community provides support for people with ${title}. You can meet others, share your experiences, and learn from others on a similar journey with ${title}.`;

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          description: metaDescription,
          title: metaTitle,
        }}
        canonical={`${process.env.NEXT_PUBLIC_CANONICAL_URL}/community/topics/${slug}`}
      />

      <SubtypeFeedScreen
        segmentId={segmentId}
        subtypeId={subtypeId}
        title={title}
        subtypeSlug={slug}
        ssrError={ssrError}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<
  unknown,
  { slug: string; segmentId?: string }
> = async (ctx) => {
  // If an error happens during SSR, we want to capture it and pass it to the client.
  let ssrError: SSRError = null;

  const queryClient = new QueryClient();

  const session = await getServerSession(ctx);
  // Pre-load the session query
  queryClient.setQueryData(getQueryKey(), session);

  const { slug } = ctx.params;
  let { segmentId } = ctx.query;

  if (Array.isArray(segmentId)) {
    [segmentId] = segmentId;
  }

  const subtypes = await fetchAllTopics();

  queryClient.setQueryData(subtypeQueryKey(), subtypes);

  const subtype = subtypes.find((item) => item.slug === slug);

  if (!subtype) {
    return {
      notFound: true,
    };
  }

  // const subtype = subtypes.find((item) => item.slug === slug);
  // If the user is not logged in, we want to pre-fill the query cache,
  // so the content is in the initial HTML. This is needed for SEO.
  if (!session) {
    try {
      const firstPage = await getPublicPosts({
        segmentId: ctx?.query?.segmentId as string,
        subtype: subtype.id,
      });

      queryClient.setQueryData(
        getPublicPostsQueryKey({
          subtype: subtype.id,
          sortBy: "Most recent",
          segmentId: ctx.query.segmentId as string,
        }),
        firstPage
      );

      const topics = await getCategorizedTopics();
      queryClient.setQueryData(getTopicsQueryKey(), topics);
    } catch (error) {
      if (
        error instanceof Error &&
        error?.name === APIErrorType.INVALID_PERMISSIONS
      ) {
        return {
          redirect: {
            destination: `/login?redirectTo=/community/posts`,
            permanent: false,
          },
        };
      }

      if (error instanceof Error) {
        ssrError = {
          name: error.name,
          message: error.message,
          stack: error.stack ?? null,
        };
      }
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      slug,
      segmentId: segmentId ?? null,
      subtypeId: subtype.id,
      title: subtype.title,
      ssrError,
    },
  };
};

// The page is publicly accessible, so we want to show the OneTrust consent
// dialog to the user if they are not logged in.
SubtypeFeedPage.shouldShowConsentDialog = true;
SubtypeFeedPage.hideNavbarShadowOnDesktop = true;
SubtypeFeedPage.hideNavbarShadowOnMobile = true;
SubtypeFeedPage.backgroundColor = "gray-v2";

export default SubtypeFeedPage;
